import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { App as AntdApp } from 'antd'; 
import { getCurrentUserId } from '../utils/auth';

export type EntityMode = 'create' | 'edit' | 'view';

export function useEntityForm<TEntity = any, TCreate = Partial<TEntity>, TUpdate = Partial<TEntity>>({
  entityName,
  getEntity,
  createEntity,
  updateEntity,
  redirectPath,
  formFields
}: {
  entityName: string;
  getEntity: (id: string) => Promise<{ data: TEntity }>;
  createEntity: (data: TCreate) => Promise<any>;
  updateEntity: (id: string, data: TUpdate) => Promise<any>;
  redirectPath: string;
  formFields?: any[]; // Optional, for dynamic forms
}) {
  const { message } = AntdApp.useApp();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const mode: EntityMode = location.pathname.includes('view')
    ? 'view'
    : location.pathname.includes('edit')
      ? 'edit'
      : 'create';

  const [data, setData] = useState<TEntity | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await getEntity(id);
      setData(res.data);
    } catch {
      message.error(`Failed to fetch ${entityName}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'create') setData(null);
    else fetch();
  }, [id, mode]);

  const handleSubmit = async (values: TCreate | TUpdate, a={}, b={}) => {
    const allowedFields = formFields?.filter(field => !field.showOn || (mode && field.showOn.includes(mode)))?.map(field=>field.name)||[]
    const payload = allowedFields.length>0?Object.fromEntries(
      Object.entries(values||{}).filter(([key]) => allowedFields.includes(key))
    ):values; 
    try {
      setLoading(true);

      const systemFields = { 
        permissions: [],
      };

      if (mode === 'edit' && id) {
        // ✅ Remove createdBy before sending update payload
        const { createdBy, createdAt, ...updatePayload } = payload as any; 
        await updateEntity(id, {
          ...updatePayload, 
          permissions: systemFields.permissions,
          updatedBy: getCurrentUserId(),
        });

        message.success(`${entityName} updated successfully`);
      } else {
        // ✅ Remove updatedBy from create payload
        const { updatedBy, updatedAt, ...createPayload } = payload as any; 
        await createEntity({
          ...createPayload, 
          permissions: systemFields.permissions,
          createdBy: getCurrentUserId(),
        });

        message.success(`${entityName} created successfully`);
      }

      navigate(redirectPath);
    } catch {
      message.error(`Failed to save ${entityName}`);
    } finally {
      setLoading(false);
    }
  };




  return {
    id,
    mode,
    data,
    loading,
    handleSubmit,
    handleReset: fetch,
    navigate,
  };
}
